import { MCAContext } from '@interhyp/module-case-assignment';
import { useApplicationContext } from '@interhyp/util-application-context';
import { calculateTheme } from '@platform/theme';
import { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';

import { grayscaleImages, isBrightColor, setCSSVariable, shadeColor } from './theme';
import {
  getCaseAssignmentConfiguration,
  getCompanyConfiguration,
  getFeatureConfiguration,
} from '../api';
import { setConfig, setConfigUsername } from '../redux/Company/actions';
import { getCompanyConfig } from '../redux/Company/selectors';
import { isAuthenticated } from '../redux/StateSession/selectors';

const setCSSVariables = config => {
  const colorPrimary = config?.style['color-primary'] || '#579EB0';
  const colorSecondary = config?.style['color-secondary'] || '#60B187';
  const colorTertiary = config?.style['color-tertiary'] || '#8BC44A';
  const colorInteraction = config?.style['color-interaction'] || '#EE7900';
  const colorBrand = config?.style['color-brand'] || '#EE7900';

  const setCalculatedThemeVariables = () => {
    const variables = {
      ...calculateTheme(colorPrimary, 'primary'),
      ...calculateTheme(colorSecondary, 'secondary'),
      ...calculateTheme(colorTertiary, 'tertiary'),
    };

    Object.entries(variables).forEach(([key, value]) => {
      setCSSVariable(key, value);
    });
  };

  Object.entries(config?.style).forEach(([propertyName, value]) => {
    if (propertyName !== 'font-type') {
      setCSSVariable(`--${propertyName}`, value);
    }
  });

  setCalculatedThemeVariables();

  if (colorInteraction) {
    const isBrightInteractionColor = isBrightColor(colorInteraction);
    setCSSVariable(
      '--color-interaction-hover',
      shadeColor(colorInteraction, isBrightInteractionColor ? -50 : -10),
    );
    setCSSVariable('--color-interaction-text', isBrightInteractionColor ? '#333' : '#fff');
  }

  if (colorBrand) {
    setCSSVariable('--color-brand-text', isBrightColor(colorBrand) ? '#333' : '#fff');
  }
};

const useConfiguration = () => {
  const dispatch = useDispatch();
  const authenticated = useSelector(isAuthenticated);
  const companyConfiguration = useSelector(getCompanyConfig);
  // get key:value permissions object showXY: bool
  const { addPermission } = useApplicationContext();
  const [, setMcaContext] = useContext(MCAContext);

  const setCompanyConfiguration = useCallback(
    companyConfig => {
      // grayscaleImages(companyConfig?.companyName !== 'interhyp');
      if (companyConfig?.style) {
        setCSSVariables(companyConfig);
      }
      if (companyConfig?.env) {
        dispatch(setConfig(companyConfig));
      }
    },
    [dispatch],
  );

  const setUserConfiguration = useCallback(
    caseAssignmentConfig => {
      if (caseAssignmentConfig?.accessibleUnits) {
        setMcaContext(prevState => ({
          ...prevState,
          accessibleUnits: caseAssignmentConfig.accessibleUnits,
        }));
      }
      if (caseAssignmentConfig?.defaultUnit) {
        setMcaContext(prevState => ({
          ...prevState,
          unitId: prevState.unitId ? prevState.unitId : caseAssignmentConfig.defaultUnit.id,
          unitName: prevState.unitId ? prevState.unitName : caseAssignmentConfig.defaultUnit.name,
        }));
      }
      if (caseAssignmentConfig?.userName) {
        setMcaContext(prevState => ({
          ...prevState,
          userName: caseAssignmentConfig.userName,
        }));
        dispatch(setConfigUsername(caseAssignmentConfig.userName));
      }
    },
    [dispatch, setMcaContext],
  );

  const setFeatureConfiguration = useCallback(
    featureConfig => {
      // we need to enable start page for every one. Until we fix this complete permission mess up, we leave it like that
      // for further information why we do that, please contact interaciton-ex
      if (!featureConfig.showPhoneWidget) {
        featureConfig.showPhoneWidget = true;
      }
      const extractedPermissions = Object.keys(featureConfig).filter(id => {
        return featureConfig[id];
      });
      addPermission(extractedPermissions);
    },
    [addPermission],
  );

  useEffect(() => {
    if (!authenticated) {
      return;
    }
    const getConfigurations = () => {
      getCompanyConfiguration()
        .then(companyConfig => {
          setCompanyConfiguration(companyConfig);
        })
        .catch(error => {
          console.log(error);
        });

      getCaseAssignmentConfiguration()
        .then(caseAssignmentConfig => {
          setUserConfiguration(caseAssignmentConfig);
        })
        .catch(error => {
          console.log(error);
        });
      getFeatureConfiguration()
        .then(featureConfig => {
          setFeatureConfiguration(featureConfig);
        })
        .catch(error => {
          console.log(error);
        });
    };
    getConfigurations();
  }, [
    addPermission,
    authenticated,
    dispatch,
    setCompanyConfiguration,
    setFeatureConfiguration,
    setUserConfiguration,
  ]);
};

export { useConfiguration };
